import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Battery, Shield, Activity, TrendingUp, MapPin, Zap } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="flex items-center justify-between px-8 py-6 max-w-7xl mx-auto border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <img src="/sodiumq-logo.png" alt="SodiumQ Logo" className="h-12 w-auto" />
        </div>
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            className="text-gray-700 hover:text-black hover:bg-gray-100"
            onClick={() => navigate('/login')}
            data-testid="nav-login-btn"
          >
            Sign In
          </Button>
          <Button 
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium px-6"
            onClick={() => navigate('/register')}
            data-testid="nav-register-btn"
          >
            Get Started
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-8 py-20 fade-in">
        <div className="text-center space-y-8">
          <div className="inline-block">
            <div className="flex items-center space-x-2 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-2 mb-6">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
              <span className="text-sm text-gray-700">Real-time Battery Intelligence Platform</span>
            </div>
          </div>
          
          <h1 className="text-6xl lg:text-7xl font-extrabold text-black leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sodium BESS<br/>
            <span className="bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">Performance Monitoring</span>
          </h1>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Cloud-native SaaS platform for monitoring large-scale sodium battery energy storage systems.
            Real-time analytics, predictive maintenance, and digital twin simulation for fixed and mobile assets.
          </p>

          <div className="flex items-center justify-center space-x-4 pt-6">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium px-8 text-lg h-14 rounded-xl"
              onClick={() => navigate('/register')}
              data-testid="hero-get-started-btn"
            >
              Start Monitoring
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-white/20 bg-white/5 hover:bg-white/10 text-white font-medium px-8 text-lg h-14 rounded-xl"
              onClick={() => navigate('/login')}
              data-testid="hero-login-btn"
            >
              View Demo
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-24">
          {[
            { label: 'Active Assets', value: '20+', icon: Battery },
            { label: 'Uptime', value: '99.9%', icon: Activity },
            { label: 'Safety Score', value: '98', icon: Shield },
            { label: 'Efficiency', value: '92%', icon: TrendingUp }
          ].map((stat, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-emerald-500 hover:shadow-lg transition-all duration-300" data-testid={`stat-card-${idx}`}>
              <stat.icon className="w-8 h-8 text-emerald-500 mb-3" />
              <div className="text-3xl font-bold text-black mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{stat.value}</div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-24">
          {[
            {
              icon: Activity,
              title: 'Real-time Monitoring',
              description: 'High-frequency telemetry ingestion with <3s data freshness. Track SOC, SOH, DOD, efficiency, and thermal metrics.'
            },
            {
              icon: Shield,
              title: 'Safety-First Analytics',
              description: 'Predictive anomaly detection with severity scoring. Early-warning system for thermal runaway and cell imbalances.'
            },
            {
              icon: MapPin,
              title: 'Fleet & Mobile Tracking',
              description: 'GPS tracking for mobile units with geofencing. Unified view across 20+ fixed and mobile assets.'
            }
          ].map((feature, idx) => (
            <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-8 hover:border-emerald-500 hover:shadow-lg transition-all duration-300 group" data-testid={`feature-card-${idx}`}>
              <div className="w-14 h-14 rounded-xl bg-emerald-50 flex items-center justify-center mb-6 group-hover:bg-emerald-100 transition-all duration-300">
                <feature.icon className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-black mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{feature.title}</h3>
              <p className="text-gray-700 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-24 text-center bg-emerald-50 border border-emerald-200 rounded-3xl p-12">
          <h2 className="text-4xl font-bold text-black mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Ready to optimize your fleet?</h2>
          <p className="text-gray-700 text-lg mb-8">Start monitoring your sodium battery systems today</p>
          <Button 
            size="lg"
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium px-10 text-lg h-14 rounded-xl"
            onClick={() => navigate('/register')}
            data-testid="cta-get-started-btn"
          >
            Get Started Now
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-24 py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8 text-center text-gray-700 text-sm">
          <p className="mb-2">© 2025 SodiumQ. Professional Sodium BESS Monitoring Platform.</p>
          <p className="text-gray-600 text-xs">
            SodiumQ is a proprietary technology platform developed by Australia's Battery Technology Investments Pty Limited (ABN 54 676 904 902).
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;